import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, BoxProps, Button, Flex, Input, Link, Text, Portal } from "@chakra-ui/react";
import { landing } from "../../landingTokens";
import { useLandingTheme } from "../../LandingThemeContext";
import { useLandingCopy } from "../../LandingLanguageContext";
import { LandingUploadErrorMessageKey } from "../../landingCopy";
import { acceptedFileTypesForWeb } from "../../../../constants/GlobalTypesAndEnumsAndConstants";
import { trackErrorWhenLoaded as trackError } from "../../../../utils/analytics/track-error-when-loaded";
import { goToApp, markLandingInteraction, trackLandingCallToActionClick, trackLandingEvent } from "../../handoff/appHandoff";
import { preloadLandingApp } from "../../handoff/preloadLandingApp";
import { buildUploadFailureTelemetry, createFileUploadPageExitMonitor, createUploadAttemptId, FileUploadError, FileUploadInputMethod } from "../../../home-page/file-upload-client";
import { usePageFileDrop } from "../../handoff/usePageFileDrop";
import { useAppUrl } from "../../handoff/useAppUrl";
import { LandingUploadError, uploadFileFromLandingPage } from "../../handoff/landingFileUpload";
import {
  LANDING_HANDOFF_CONTENT_MEDIUM_TYPE_QUERY_PARAM,
  LANDING_HANDOFF_OBJECT_KEY_QUERY_PARAM,
  LANDING_HANDOFF_STORED_FILE_NAME_QUERY_PARAM,
  LANDING_HANDOFF_UPLOAD_CLAIM_FRAGMENT_PARAM,
} from "../../../home-page/landingHandoffUploadClaim";

// Only the site a pasted link points at is recorded (e.g. "youtube.com"), never the link itself.
function getDomainOfPastedLink(pastedLink: string): string | null {
  try {
    const linkWithProtocol = /^https?:\/\//i.test(pastedLink) ? pastedLink : `https://${pastedLink}`;
    return new URL(linkWithProtocol).hostname.replace(/^www\./, "") || null;
  } catch {
    return null;
  }
}

function UploadIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 13V4m0 0L6.5 7.5M10 4l3.5 3.5M4 13.5V15a1.5 1.5 0 001.5 1.5h9A1.5 1.5 0 0016 15v-1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M3 9h12m0 0L10 4m5 5l-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// A pasted link (as opposed to typed) is at least this long; the Framer site submitted those right away.
const PASTED_LINK_MIN_LENGTH = 6;

// Upload first, as the primary action on its own line; pasting a link sits underneath as the
// quieter alternative. Both hand the visitor to the app: the file is uploaded and its processing
// task created here, so the app can pick the result up on arrival; a link goes across as-is.
export function UploadStacked({ onDark = false, ...props }: BoxProps & { onDark?: boolean }) {
  const theme = useLandingTheme();
  const uploadCopy = useLandingCopy().upload;
  const signUpHref = useAppUrl(landing.signUpUrl);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadRootRef = useRef<HTMLDivElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const uploadingRef = useRef(false);
  const [uploadStage, setUploadStage] = useState<"preparing" | "uploading" | "processing">("preparing");
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  // The reason rather than a message: LandingUploadError's own message is English (it is what Sentry
  // records), while the visitor reads the message for this reason in the page's language.
  const [uploadFailureReason, setUploadFailureReason] = useState<LandingUploadErrorMessageKey | null>(null);
  const [linkInput, setLinkInput] = useState("");
  const [intakeReady, setIntakeReady] = useState(false);

  // After the hand-off to app.jungleai.com the browser may later restore this page from the
  // back/forward cache (visitor presses Back). That restore brings React state back exactly as it
  // was, so without this reset the button would stay stuck on "Uploading…" and refuse a new upload.
  useEffect(() => {
    // Static marketing HTML is visible before its handlers are attached.
    setIntakeReady(true);
    const resetUploadStateAfterBackForwardCacheRestore = (event: PageTransitionEvent) => {
      if (event.persisted) { uploadingRef.current = false; setIsUploading(false); }
    };
    const warnBeforeLeavingActiveUpload = (event: BeforeUnloadEvent) => {
      if (!uploadingRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("pageshow", resetUploadStateAfterBackForwardCacheRestore);
    window.addEventListener("beforeunload", warnBeforeLeavingActiveUpload);
    return () => {
      window.removeEventListener("pageshow", resetUploadStateAfterBackForwardCacheRestore);
      window.removeEventListener("beforeunload", warnBeforeLeavingActiveUpload);
    };
  }, []);

  const linkBarBg = onDark ? "rgba(255, 255, 255, 0.08)" : theme.color.inputBg;
  const linkBarBorder = onDark ? "1px solid rgba(255, 255, 255, 0.18)" : `1px solid ${theme.color.border}`;
  const linkTextColor = onDark ? "#FFFFFF" : theme.color.inputText;
  const linkPlaceholderColor = onDark ? "rgba(255, 255, 255, 0.55)" : theme.color.muted;
  const errorTextColor = onDark ? "#FFD2CC" : "#B42318";

  const openFilePicker = () => {
    if (uploadingRef.current) return;
    markLandingInteraction();
    trackLandingCallToActionClick("upload_slides");
    fileInputRef.current?.click();
  };

  const uploadFiles = async (files: File[], inputMethod: FileUploadInputMethod) => {
    if (uploadingRef.current) return;
    markLandingInteraction();
    if (inputMethod === "drag-and-drop") uploadRootRef.current?.scrollIntoView?.({ block: "center" });
    if (files.length !== 1) {
      setUploadFailureReason(files.length ? "multiple_files" : "unsupported_file_type");
      return;
    }
    const chosenFile = files[0];
    uploadingRef.current = true;
    setUploadStage("preparing");
    setUploadPercent(null);
    const attemptId = createUploadAttemptId();
    const startedAt = performance.now();
    setUploadFailureReason(null);
    setIsUploading(true);
    // File names and pasted URLs are user content: the analytics catalog forbids them on product events, so
    // only the extension, size and type are recorded. (Upload failures still carry the name to Sentry.)
    const fileExtension = chosenFile.name.includes(".") ? chosenFile.name.split(".").pop()?.toLowerCase() ?? null : null;
    trackLandingEvent("Landing Page: File Upload Started", { fileExtension, fileSizeBytes: chosenFile.size, fileType: chosenFile.type, inputMethod, landingVariant: theme.id });
    let pendingStage = "preparing";
    const exitMonitor = createFileUploadPageExitMonitor({ onPageExit: () => {
      // Best-effort diagnostics, not a failed-upload event: a closed page may
      // have already delivered its bytes, and a killed browser may report nothing.
      trackError(new Error("Landing upload interrupted by page exit"), {
        scope: "landing-upload-page-exit", level: "warning",
        tags: { ingestion_attempt_id: attemptId, landing_upload_stage: pendingStage, input_method: inputMethod },
        extra: { fileExtension, fileSizeBytes: chosenFile.size, durationMs: Math.round(performance.now() - startedAt) },
      });
    } });
    try {
      const landingUploadResult = await uploadFileFromLandingPage(chosenFile, {
        attemptId, inputMethod, onStage: stage => {
          pendingStage = stage;
          setUploadStage(stage);
          if (stage === "uploading") preloadLandingApp();
        },
        onUploadProgress: ({ progressPercent }) => setUploadPercent(Math.round(progressPercent)),
      });
      const { fileTaskId, fileName } = landingUploadResult;
      exitMonitor.markSettled();
      trackLandingEvent("Landing Page: File Upload Succeeded", { fileExtension, contentMediumType: landingUploadResult.contentMediumType,
        inputMethod, landingVariant: theme.id, duration_ms: Math.round(performance.now() - startedAt), outcome: "storage_and_task_handoff" });
      // Keep the visual loading state while allowing the completed handoff to navigate.
      uploadingRef.current = false;
      goToApp(
        "/",
        {
          fileTaskId,
          fileName,
          landingHandoffId: fileTaskId,
          landingVariant: theme.id === "moss" || theme.id === "clearing" ? theme.id : "",
          [LANDING_HANDOFF_STORED_FILE_NAME_QUERY_PARAM]: landingUploadResult.storedFileName,
          [LANDING_HANDOFF_OBJECT_KEY_QUERY_PARAM]: landingUploadResult.uploadedFileS3ObjectKey,
          [LANDING_HANDOFF_CONTENT_MEDIUM_TYPE_QUERY_PARAM]: landingUploadResult.contentMediumType,
        },
        navigate,
        landingUploadResult.uploadClaimToken
          ? { [LANDING_HANDOFF_UPLOAD_CLAIM_FRAGMENT_PARAM]: landingUploadResult.uploadClaimToken }
          : {}
      );
    } catch (error) {
      exitMonitor.markSettled();
      const failureReason: LandingUploadErrorMessageKey = error instanceof LandingUploadError ? error.reason : "unknown";
      const cause = error instanceof LandingUploadError ? error.cause : error;
      const uploadTelemetry = cause instanceof FileUploadError ? buildUploadFailureTelemetry({
        error: cause, attemptId, inputMethod, file: chosenFile, contentMediumType: null,
        stableUploadedFileIdentifiers: error instanceof LandingUploadError ? error.sourceIdentifiers : undefined,
      }) : {};
      trackError(error, {
        scope: "landing-page-file-upload",
        level: ["empty_file", "unsupported_file_type", "multiple_files"].includes(failureReason) ? "info" : "error",
        tags: { ingestion_attempt_id: attemptId, failure_reason: failureReason, input_method: inputMethod,
          landing_variant: theme.id, ...(cause instanceof FileUploadError ? {
            upload_stage: cause.stage, error_code: cause.code,
            storage_error_code: cause.storageErrorCode || "missing", body_materialized: String(cause.bodyMaterialized),
          } : {}) },
        extra: { reason: failureReason, fileName: chosenFile.name, fileSizeInBytes: chosenFile.size,
          fileType: chosenFile.type, ...uploadTelemetry, isOnline: navigator.onLine },
      });
      // Counted next to "Started" and "Succeeded" so the upload success rate can be read per design.
      trackLandingEvent("Landing Page: File Upload Failed", { failureReason, fileExtension, fileSizeBytes: chosenFile.size, inputMethod, landingVariant: theme.id, duration_ms: Math.round(performance.now() - startedAt) });
      setUploadFailureReason(failureReason);
      uploadingRef.current = false;
      setIsUploading(false);
    } finally {
      exitMonitor.dispose();
    }
  };

  const handleFileChosen = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length) void uploadFiles(files, "file-picker");
  };
  const isDragging = usePageFileDrop((files) => void uploadFiles(files, "drag-and-drop"), isUploading);
  const loadingLabel = uploadStage === "preparing" ? uploadCopy.preparingButtonLabel
    : uploadStage === "processing" ? uploadCopy.processingButtonLabel
    : uploadPercent === null ? uploadCopy.uploadingButtonLabel : `${uploadCopy.uploadingButtonLabel} ${uploadPercent}%`;

  const submitLink = (pageUrl: string) => {
    if (uploadingRef.current) return;
    const trimmedPageUrl = pageUrl.trim();
    if (!trimmedPageUrl) return;
    markLandingInteraction();
    trackLandingCallToActionClick("paste_link", { pastedLinkDomain: getDomainOfPastedLink(trimmedPageUrl) });
    goToApp("/", { url: trimmedPageUrl, landingHandoffId: createUploadAttemptId(),
      landingVariant: theme.id === "moss" || theme.id === "clearing" ? theme.id : "" }, navigate);
  };

  return (
    <Box ref={uploadRootRef} w="100%" {...props} data-landing-intake-ready={intakeReady ? "true" : "false"}>
      {isDragging && (
        <Portal>
          <Flex data-testid="landing-file-drop" role="status" aria-live="polite" position="fixed" inset={0}
            zIndex={1800} align="center" justify="center" pointerEvents="none" p={{ base: "16px", md: "32px" }}
            bg="rgba(16, 38, 19, 0.32)" backdropFilter="blur(6px)">
            <Flex position="absolute" inset={{ base: "12px", md: "24px" }} border="2px dashed #669442"
              borderRadius={{ base: "24px", md: "36px" }} bg="rgba(245, 251, 236, 0.94)" align="center" justify="center" p="24px">
              <Flex direction="column" align="center" textAlign="center" maxW="480px" gap="20px">
                <Flex aria-hidden="true" w="88px" h="104px" bg="white" border="1px solid #CBDFC0" borderRadius="18px"
                  align="center" justify="center" color="#477829" transform="rotate(-6deg)"
                  boxShadow="8px 8px 0 #DFECCD, 0 12px 32px rgba(35, 63, 28, 0.10)">
                  <UploadIcon />
                </Flex>
                <Box mt="8px">
                  <Text fontFamily={theme.font.display} fontSize={{ base: "32px", md: "46px" }} fontWeight={700}
                    lineHeight={1.15} letterSpacing="-0.03em" color="#243E1D">{uploadCopy.dropTitle}</Text>
                  <Text mt="14px" fontFamily={theme.font.body} fontSize={{ base: "16px", md: "18px" }}
                    lineHeight={1.6} color="#53654A">{uploadCopy.dropDescription}</Text>
                </Box>
              </Flex>
            </Flex>
          </Flex>
        </Portal>
      )}
      <input ref={fileInputRef} type="file" accept={acceptedFileTypesForWeb} onChange={handleFileChosen} style={{ display: "none" }} aria-hidden="true" tabIndex={-1} />
      <Button
        w="100%"
        minH="64px"
        h="auto"
        py="12px"
        px="26px"
        whiteSpace="normal"
        lineHeight={1.4}
        justifyContent="flex-start"
        bg={theme.color.accent}
        color={theme.button.primaryColor}
        border={theme.button.border}
        fontFamily={theme.font.body}
        fontSize="20px"
        fontWeight={600}
        borderRadius={theme.shape.buttonRadius}
        leftIcon={<UploadIcon />}
        iconSpacing="12px"
        transition={landing.buttonHoverTransition}
        _hover={{ bg: theme.color.accentHover, transform: theme.button.hoverTransform }}
        _focusVisible={{ boxShadow: `0 0 0 3px ${theme.color.highlight}` }}
        // Chakra fades a loading button to 40%, which reads as broken on the page's main action.
        _loading={{ opacity: 0.9, cursor: "progress" }}
        _disabled={{ opacity: 0.9, cursor: "progress" }}
        isLoading={isUploading}
        loadingText={<Box as="span" minW={0} overflowWrap="anywhere" textAlign="start">{loadingLabel}</Box>}
        spinnerPlacement="start"
        onClick={openFilePicker}
      >
        <Box as="span" minW={0} overflowWrap="anywhere" textAlign="start">{uploadCopy.uploadSlidesButton}</Box>
      </Button>

      <Flex align="center" mt="14px" bg={linkBarBg} border={linkBarBorder} borderRadius={theme.shape.buttonRadius} p="6px" pl="26px" h="56px">
        <Input
          variant="unstyled"
          placeholder={uploadCopy.pasteLinkPlaceholder}
          aria-label={uploadCopy.pasteLinkInputAriaLabel}
          fontFamily={theme.font.body}
          fontSize="17px"
          color={linkTextColor}
          _placeholder={{ color: linkPlaceholderColor }}
          minW={0}
          value={linkInput}
          isDisabled={isUploading}
          onFocus={markLandingInteraction}
          autoComplete="off"
          spellCheck={false}
          inputMode="url"
          dir="ltr"
          onChange={(event) => setLinkInput(event.target.value)}
          onPaste={(event) => {
            // A link pasted into the empty field goes straight to the app, as on the Framer site.
            // Only a real paste counts: typing, autofill and drag-and-drop leave the visitor in control.
            const pastedText = event.clipboardData.getData("text").trim();
            if (linkInput.length === 0 && pastedText.length >= PASTED_LINK_MIN_LENGTH) {
              event.preventDefault();
              setLinkInput(pastedText);
              submitLink(pastedText);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") submitLink(linkInput);
          }}
        />
        <Button
          aria-label={uploadCopy.submitLinkButtonAriaLabel}
          flexShrink={0}
          h="100%"
          w="42px"
          minW={0}
          p={0}
          bg={onDark ? "rgba(255, 255, 255, 0.12)" : theme.color.accentSoft}
          color={onDark ? "#FFFFFF" : theme.color.accentDark}
          borderRadius={theme.shape.buttonRadius === "999px" ? "999px" : `calc(${theme.shape.buttonRadius} - 3px)`}
          _hover={{ bg: onDark ? "rgba(255, 255, 255, 0.2)" : theme.color.border }}
          _focusVisible={{ boxShadow: `0 0 0 3px ${theme.color.highlight}` }}
          isDisabled={isUploading || linkInput.trim().length === 0}
          onClick={() => submitLink(linkInput)}
        >
          <ArrowIcon />
        </Button>
      </Flex>

      {/* Phones only (owner, 2026-10-27: "should only be a mobile thing"): a visitor at a computer has their files
          at hand. Hidden by CSS, never by branching the render, so the prerendered page and hydration agree. */}
      <Text display={{ base: "block", md: "none" }} mt="16px" textAlign="left" fontFamily={theme.font.body} fontSize="15px" lineHeight={1.5} color={onDark ? "rgba(255, 255, 255, 0.78)" : theme.color.muted}>
        {uploadCopy.noFilePrompt}{" "}
        <Link href={signUpHref} color={onDark ? "#FFFFFF" : theme.color.accentDark} fontWeight={600} textDecoration="underline"
          textUnderlineOffset="3px" _hover={{ color: onDark ? "#FFFFFF" : theme.color.accent }}
          _focusVisible={{ boxShadow: `0 0 0 3px ${theme.color.highlight}` }}
          onClick={() => trackLandingCallToActionClick("sign_up", { placement: "hero_no_file" })}>
          {uploadCopy.signUpAndAddLater}
        </Link>
      </Text>

      {uploadFailureReason && (
        <Text role="alert" mt="12px" fontFamily={theme.font.body} fontSize="14px" lineHeight={1.5} color={errorTextColor}>
          {uploadCopy.errorMessageByFailureReason[uploadFailureReason]}
        </Text>
      )}
    </Box>
  );
}
